import { Loader2 } from 'lucide-react';
import { Input } from './Input';
import { Select } from './Select';

export interface CategoryAutoActionOption {
  value: string;
  label: string;
}

export interface CategoryAutoActionRowProps {
  /** Etykieta kategorii wyświetlana jako pill. */
  badgeLabel: string;
  /** Klasy Tailwind dla pill'a (paleta per-kategoria, świadoma semantyka). */
  badgeClassName: string;
  /** Priorytet 0-100. */
  priority: number;
  onPriorityChange: (value: number) => void;
  /** Wybrana akcja ("none" lub nazwa). Null = "none". */
  autoAction: string | null;
  onAutoActionChange: (value: string | null) => void;
  /** Lista opcji akcji. Pierwsza powinna być domyślnie "none". */
  options: CategoryAutoActionOption[];
  /** Spinner zapisu w prawym górnym rogu. */
  saving?: boolean;
  className?: string;
}

/**
 * Wiersz konfiguracji auto-akcji dla kategorii (używany w kilku miejscach
 * w Ustawieniach Wiadomości, min. AI Kategorie). Layout: pill kategorii
 * w headerze + dwa pola (Priorytet input + Akcja select).
 *
 * Paleta kategorii (badgeClassName) pozostaje custom — to świadoma semantyka
 * biznesowa per-kategoria (tracking = info-blue, return = warning, itd.),
 * nie brand UI pattern.
 */
export function CategoryAutoActionRow({
  badgeLabel,
  badgeClassName,
  priority,
  onPriorityChange,
  autoAction,
  onAutoActionChange,
  options,
  saving = false,
  className,
}: CategoryAutoActionRowProps) {
  return (
    <div
      className={[
        'p-3 rounded-xl bg-surface-raised/30 border border-border-subtle hover:border-border transition-colors duration-fast',
        className,
      ].filter(Boolean).join(' ')}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`px-2 py-0.5 text-xs font-medium rounded-badge border ${badgeClassName}`}>
          {badgeLabel}
        </span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-text-secondary" />}
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <label className="text-xxs text-text-muted w-14 flex-shrink-0">Priorytet:</label>
          <Input
            type="number"
            min={0}
            max={100}
            value={priority}
            onChange={e => {
              const val = parseInt(e.target.value) || 50;
              onPriorityChange(Math.max(0, Math.min(100, val)));
            }}
            className="flex-1"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xxs text-text-muted w-14 flex-shrink-0">Akcja:</label>
          <Select
            value={autoAction || 'none'}
            onChange={e => onAutoActionChange(e.target.value === 'none' ? null : e.target.value)}
            className="flex-1"
          >
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
}
